/**
 * RatingListPresenter
 * --
 */
import React from 'react';
import dayjs from 'dayjs';
import { Content, Title, Row, Col, ModalLayout, Text } from 'components';
import { Button, Table, Tag, Spin, Flex, Input, Space, Tabs } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { stringToMoneyFormat } from 'utils';
import { RatingRegister } from '..';
import { ACCOUNT_STATUS, SIGNUP_PATH } from 'constants/UserContants';
import { Link, useNavigate } from 'react-router-dom';
import { encrypt } from 'utils/Crypto';
import Formatter from 'utils/Formatter';
import { downloadXlsx } from 'utils/XlsxDownloader';

const RatingListPresenter = (props) => {
  /* ===== ROUTE ===== */
  const navigate = useNavigate();

  /* ===== PROPS ===== */
  const {
    grades,
    coupons,
    isLoading,
    isOpened,
    setIsOpened,
    setIsLoading,
    onChangeSearchValue,
    searchValue,
    onResetSearchValue,
    onReload,
  } = props;

  /* ===== STATE ===== */
  const [modalData, setModalData] = React.useState(null);
  const [usersModalData, setUsersModalData] = React.useState(null);
  const [gradeName, setGradeName] = React.useState(null);
  const [tabKey, setTabKey] = React.useState('Y');
  /* ===== VARIABLE ===== */

  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };

  const columns = React.useMemo(() => {
    return [
      {
        key: 'grade_id',
        title: '등급번호',
        dataIndex: 'grade_id',
      },
      {
        key: 'grade_name',
        title: '등급명',
        dataIndex: 'grade_name',
      },
      {
        key: 'grade_reason',
        title: '등급변경사유',
        dataIndex: 'grade_reason',
      },
      {
        key: 'grade_description_standard',
        title: '등급기준',
        dataIndex: 'grade_description',
        render: (item) => item?.['기준'] ?? '-',
      },
      {
        key: 'grade_description_description',
        title: '등급설명',
        dataIndex: 'grade_description',
        render: (item) => item?.['설명'] ?? '-',
      },
      {
        key: 'grade_condition_amount',
        title: '등급 기준금액',
        dataIndex: 'grade_condition_amount',
        render: (item) => stringToMoneyFormat(item, '원'),
      },
      {
        key: 'transaction_count',
        title: '등급 거래건수',
        dataIndex: 'transaction_count',
        render: (item) => stringToMoneyFormat(item, '건'),
      },
      {
        key: 'is_enable',
        title: '사용여부',
        dataIndex: 'is_enable',
        render: (item) => (
          <Tag color={item === 'Y' ? 'processing' : 'default'}>
            {item === 'Y' ? '사용중' : '미사용'}
          </Tag>
        ),
      },
      {
        key: 'is_fixed',
        title: '고정등급여부',
        dataIndex: 'is_fixed',
        render: (item) => <Tag>{item === 'Y' ? '고정' : '변동'}</Tag>,
      },
      {
        key: 'couponList',
        title: '등급 혜택',
        dataIndex: 'couponList',
        width: 500,
        render: (item) =>
          item?.map((coupon) => (
            <p>
              - {coupon?.coupon_name || '삭제된 쿠폰'} [{coupon?.num}개]
            </p>
          )),
      },
      {
        key: 'user',
        title: '회원',
        dataIndex: 'user',
        width: 100,
        render: (user, record) => (
          <a
            onClick={() => {
              setUsersModalData(user);
              setGradeName(record.grade_name);
            }}
          >
            {stringToMoneyFormat(user?.length, '명')}
          </a>
        ),
        onCell: () => ({
          onClick: (e) => e.stopPropagation(), // 클릭 이벤트 전파 방지
        }),
      },
    ];
  }, []);

  const modalColumns = [
    {
      key: 'user_id',
      title: '회원번호',
      dataIndex: 'user_id',
      render: (user_id) => (
        <Link
          target="_blank"
          to={`/users/list?modal=${encodeURIComponent(encrypt(user_id))}`}
        >
          {user_id}
        </Link>
      ),
      sorter: (a, b) => a.user_id - b.user_id,
    },
    {
      key: 'user_name',
      title: '회원명',
      dataIndex: 'user_name',
      render: (user_name) => user_name ?? '-',
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={searchValue.user_name}
            onChange={(e) => {
              onChangeSearchValue('user_name', e.target.value);
            }}
            onPressEnter={() => confirm()}
            placeholder={`회원명 검색`}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              검색
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                onChangeSearchValue('user_name', '');
              }}
              size="small"
              style={{ width: 90 }}
            >
              초기화
            </Button>
          </Space>
        </div>
      ),
    },
    {
      key: 'membership_code',
      title: '멤버십 번호',
      dataIndex: 'membership_code',
      render: (membership_code) => Formatter.membership(membership_code) ?? '-',

      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={searchValue.membership_code}
            onChange={(e) => {
              onChangeSearchValue('membership_code', e.target.value);
            }}
            onPressEnter={() => confirm()}
            placeholder={`맴버쉽 번호 검색`}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              검색
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                onChangeSearchValue('membership_code', '');
              }}
              size="small"
              style={{ width: 90 }}
            >
              초기화
            </Button>
          </Space>
        </div>
      ),
    },
    {
      key: 'user_account',
      title: '회원아이디',
      dataIndex: 'user_account',
      render: (user_account) => user_account ?? '-',
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={searchValue.user_account}
            onChange={(e) => {
              onChangeSearchValue('user_account', e.target.value);
            }}
            onPressEnter={() => confirm()}
            placeholder={`아이디 검색`}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              검색
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                onChangeSearchValue('user_account', '');
              }}
              size="small"
              style={{ width: 90 }}
            >
              초기화
            </Button>
          </Space>
        </div>
      ),
    },
    {
      key: 'account_status',
      title: '계정상태',
      dataIndex: 'account_status',
      render: (account_status) => (
        <Tag color={ACCOUNT_STATUS[account_status]?.color}>
          {ACCOUNT_STATUS[account_status]?.title}
        </Tag>
      ),
      filters: Object.entries(ACCOUNT_STATUS).map(([key, value]) => ({
        text: value.title,
        value: key,
      })),
      onFilter: (value, { account_status }) => account_status === value,
    },
    {
      key: 'user_phone',
      title: '연락처',
      dataIndex: 'user_phone',
      render: (user_phone) => user_phone ?? '-',
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={searchValue.user_phone}
            onChange={(e) => {
              onChangeSearchValue('user_phone', e.target.value);
            }}
            onPressEnter={() => confirm()}
            placeholder={`연락처 검색`}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              검색
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                onChangeSearchValue('user_phone', '');
              }}
              size="small"
              style={{ width: 90 }}
            >
              초기화
            </Button>
          </Space>
        </div>
      ),
    },
    {
      key: 'user_email',
      title: '이메일',
      dataIndex: 'user_email',
      render: (user_email) => user_email ?? '-',
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={searchValue.user_email}
            onChange={(e) => {
              onChangeSearchValue('user_email', e.target.value);
            }}
            onPressEnter={() => confirm()}
            placeholder={`이메일 검색`}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              검색
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                onChangeSearchValue('user_email', '');
              }}
              size="small"
              style={{ width: 90 }}
            >
              초기화
            </Button>
          </Space>
        </div>
      ),
    },
    {
      key: 'user_gender',
      title: '성별',
      dataIndex: 'user_gender',
      render: (user_gender) => (user_gender === 'M' ? '남' : '여'),
      filters: [
        { text: '남', value: 'M' },
        { text: '여', value: 'F' },
      ],
      onFilter: (value, { user_gender }) => user_gender === value,
    },
    {
      key: 'user_birthday',
      title: '생년월일',
      dataIndex: 'user_birthday',
      render: (user_birthday) => dayjs.unix(user_birthday).format('YYYY.MM.DD'),
      sorter: (a, b) => a.user_birthday - b.user_birthday,
    },
    {
      key: 'user_nickname',
      title: '닉네임',
      dataIndex: 'user_nickname',
      render: (user_nickname) => user_nickname ?? '-',
    },

    {
      key: 'created_at',
      title: '가입일',
      dataIndex: 'created_at',
      render: (created_at) =>
        dayjs.unix(created_at).format('YYYY.MM.DD HH:mm:ss'),
      sorter: (a, b) => a.created_at - b.created_at,
    },
    {
      key: 'access_time',
      title: '최근 접속일',
      dataIndex: 'access_time',
      render: (access_time) =>
        dayjs.unix(access_time).format('YYYY.MM.DD HH:mm:ss'),
      sorter: (a, b) => a.access_time - b.access_time,
    },
    {
      key: 'signup_path',
      title: '가입경로',
      dataIndex: 'signup_path',
      render: (_, recode) =>
        recode?.social_type
          ? SIGNUP_PATH[recode?.social_type]
          : recode?.user_name
          ? SIGNUP_PATH['MEMBER']
          : SIGNUP_PATH['NONE'],
    },
    {
      key: 'address',
      title: '주소',
      dataIndex: 'address',
      render: (_, recode) => (
        <Flex justify="space-between" align="center">
          <Text size={14} maxLine={2}>{`(${recode?.postcode ?? '-'}) ${
            recode?.user_address ?? '-'
          }\n${recode?.user_address_detail ?? ''}`}</Text>
        </Flex>
      ),
    },
  ];

  /* ===== FUNCTION ===== */

  /* ===== HOOKS ===== */
  const newGrades = React.useMemo(() => {
    let data = grades;
    return (data = data.filter((grade) => grade?.is_enable === tabKey));
  }, [grades, tabKey]);
  /**
   * 필터 데이터
   * --
   * */
  const newUsersModalData = React.useMemo(() => {
    let arr = usersModalData;

    const { user_name, membership_code, user_account, user_phone, user_email } =
      searchValue;
    if (user_name) {
      arr = arr?.filter((user) => user?.user_name?.includes(user_name));
    }
    if (membership_code) {
      arr = arr.filter((user) =>
        user?.membership_code?.includes(membership_code)
      );
    }
    if (user_account) {
      arr = arr?.filter((user) => user?.user_account?.includes(user_account));
    }
    if (user_phone) {
      arr = arr?.filter((user) =>
        user?.user_phone
          ?.replace(/-/g, '')
          ?.includes(user_phone?.replace(/-/g, ''))
      );
    }
    if (user_email) {
      arr = arr?.filter((user) => user?.user_email?.includes(user_email));
    }
    return arr;
  }, [usersModalData, searchValue]);

  /* ===== RENDER ===== */
  return (
    <React.Fragment>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row>
            <Col x={24}>
              <Title style={{ ...styles.title }}>회원등급 관리</Title>

              {/* 검색결과 */}
              <Content maxWidth={'100%'} borderRadius={4} padding={20}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 20,
                  }}
                >
                  <h4
                    style={{ fontSize: 14, fontWeight: 500, marginRight: 20 }}
                  >
                    총 <span>{newGrades?.length}</span>건
                  </h4>
                  <Button
                    style={{ marginRight: 8 }}
                    onClick={() => {
                      setIsOpened('CREATE');
                      setModalData(null);
                    }}
                  >
                    등급 추가
                  </Button>

                  <Button
                    style={{ marginRight: 8 }}
                    onClick={() => navigate('/users/rating/setting')}
                  >
                    상품별 등급 혜택관리
                  </Button>
                </div>
                <Tabs
                  activeKey={tabKey}
                  items={[
                    {
                      key: 'Y',
                      label: '사용',
                    },

                    {
                      key: 'N',
                      label: '미사용',
                    },
                  ]}
                  onChange={(key) => {
                    setTabKey(key);
                  }}
                />

                <Table
                  columns={columns}
                  dataSource={newGrades}
                  onRow={(record) => {
                    return {
                      onClick: () => {
                        setModalData(record);
                        setIsOpened('UPDATE');
                      },
                    };
                  }}
                />
              </Content>
              {/* 검색결과 끝 */}
            </Col>
          </Row>
        </Content>
      </Spin>

      <ModalLayout
        open={isOpened && (isOpened === 'CREATE' || isOpened === 'UPDATE')}
        onCancel={() => {
          setModalData({});
          setTimeout(() => {
            setIsOpened(null);
          }, 100);
        }}
        title={isOpened === 'CREATE' ? `등급 생성` : `등급 수정`}
        centered
        width={768}
      >
        <RatingRegister
          type={isOpened}
          data={modalData}
          coupons={coupons}
          onReload={onReload}
          onSuccess={() => {
            setModalData(null);
            setTimeout(() => {
              setIsOpened(null);
            }, 100);
          }}
          setIsLoading={setIsLoading}
        />
      </ModalLayout>

      {/* === 회원 리스트 모달 === */}
      <ModalLayout
        type={'drawer'}
        open={usersModalData}
        onCancel={() => {
          onResetSearchValue();
          setUsersModalData(null);
        }}
        title={'회원 목록'}
        width={'100%'}
        maxWidth={1500}
      >
        <Content maxWidth={'100%'} padding={0}>
          <Row>
            {/* === 모달 > 상세보기: start === */}
            <Col x={24}>
              <Flex vertical={true} gap={5}>
                <Flex gap={5}>
                  <Title size={5} style={{ marginBottom: 5 }}>
                    회원 목록
                  </Title>
                  <Button
                    onClick={() => {
                      downloadXlsx({
                        columns: modalColumns,
                        rows: usersModalData.map((user) => {
                          return {
                            ...user,
                            account_status:
                              ACCOUNT_STATUS[user.account_status].title,
                            social_type: user?.social_type
                              ? SIGNUP_PATH[user?.social_type]
                              : user?.user_name
                              ? SIGNUP_PATH['MEMBER']
                              : SIGNUP_PATH['NONE'],
                            address: `(${user?.postcode ?? '-'}) ${
                              user?.user_address ?? '-'
                            }\n${user?.user_address_detail ?? ''}`,
                          };
                        }),
                        fileName: `${gradeName}_회원목록`,
                      });
                    }}
                  >
                    엑셀로 다운로드
                  </Button>
                </Flex>

                <Table
                  scroll={{
                    x: 'max-content',
                  }}
                  defaultPageSize={50}
                  dataSource={newUsersModalData}
                  columns={modalColumns}
                />
              </Flex>

              {/* === 모달 > 상세보기 > 주문상세보기: end === */}
            </Col>
          </Row>
        </Content>
      </ModalLayout>
      {/* === 회원 리스트 모달 === */}
    </React.Fragment>
  );
};

export default RatingListPresenter;
